import { Component, OnInit, Input, OnDestroy } from '@angular/core';

import * as moment from 'moment';
import 'moment/locale/ru';

import { ILeafletObject } from '../core/index';
import { LeafletStop, ScheduledStop, Stop, StopSelectionType } from '../core/stop';
import { LeafletRail } from '../core/rail';
import { LeafletTram, Tram } from '../core/tram';
import { Route, BuiltRoute, EnhancedBuiltRoute } from '../core/route';
import { TramStop } from '../core/tram-stop';
import { StopCollector } from './stop-collector';
import { RailCollector } from './rail-collector';
import { TramCollector } from './tram-collector';
import { RouteCollector } from './route-collector';
import { ScheduleCollector } from './schedule-collector';
import { TramApiService } from '../tram-api.service';
import { PanelDataExchangeService } from './panel-data-exchange.service';
import { Schedule } from '../core/schedule';
import { Segment } from '../core/segment';
import { ParamMap, ActivatedRoute } from '@angular/router';

@Component({
    selector: 'info-panel',
    templateUrl: './info-panel.component.html',
    styleUrls: ['./panel.scss', './info-panel.component.scss']
})
export class InfoPanelComponent implements OnInit, OnDestroy {
    private afterInitializeStopsHandler: (stops: LeafletStop[]) => void;
    private afterSelectStopHandler: (stop: LeafletStop) => void;
    private afterSelectRailHandler: (data: any) => void;
    private beforeSelectTramHandler: (data: LeafletTram) => void;
    private afterSelectTramHandler: (data: LeafletTram) => void;
    private nextStopHandler: (data: any) => void;
    private showRouteInfoPanelHandler: (isShown: boolean) => void;
    private afterBuildSegmentedRoute: (builtRoute: EnhancedBuiltRoute) => void;

    private _mapObject: ILeafletObject;

    private activatedRouteParams: ParamMap;

    @Input() isRouteMode: boolean = false;

    routes: Route[];
    MapObjectType = MapObjectType;
    get mapObject(): ILeafletObject {
        return this._mapObject;
    }
    set mapObject(value: ILeafletObject) {
        this._mapObject = value;

        if (!value) {
            this.mapObjectType = MapObjectType.Unknown;
        }
        else if (value instanceof LeafletTram) {
            this.mapObjectType = MapObjectType.Tram;
        }
        else if (value instanceof LeafletStop) {
            this.mapObjectType = MapObjectType.Stop;
        }
        else if (value instanceof LeafletRail) {
            this.mapObjectType = MapObjectType.Rail;
        }
    }
    mapObjectType: MapObjectType = MapObjectType.Unknown;
    selectedRoutes: Route[];
    selectedRoute: Route;
    stops: ScheduledStop[];
    outerStops: ScheduledStop[];
    builtRoutes: EnhancedBuiltRoute[];

    isDisabled: boolean = false;
    isReverse: boolean = false;

    isBuildRoutePanelOpened: boolean = false;
    isLoading: boolean = false;

    arrivalStop: Stop;
    departureStop: Stop;

    constructor(
        private activatedRoute: ActivatedRoute,
        private stopCollector: StopCollector,
        private railCollector: RailCollector,
        private tramCollector: TramCollector,
        private routeCollector: RouteCollector,
        private scheduleCollector: ScheduleCollector,
        private api: TramApiService,
        private exchanger: PanelDataExchangeService) {
        activatedRoute.paramMap.subscribe(p => {
            if (p.keys.length <= 0)
                return;

            this.activatedRouteParams = p;
        });
    }

    ngOnInit() {
        this.stops = [];
        this.outerStops = [];
        this.selectedRoutes = [];
        this.builtRoutes = [];

        moment.locale('ru');

        this.scheduleCollector.init();

        this.afterInitializeStopsHandler = _ => this.onAfterInitializeStops();
        this.stopCollector.afterInitialize.on(this.afterInitializeStopsHandler);
        this.afterSelectStopHandler = (stop: LeafletStop) => this.onAfterSelectStop(stop);
        this.stopCollector.afterSelect.on(this.afterSelectStopHandler);
        this.afterSelectRailHandler = (data: any) => this.onAfterSelectRail(data.event, data.rail);
        this.railCollector.afterSelect.on(this.afterSelectRailHandler);
        this.beforeSelectTramHandler = (tram: LeafletTram) => this.onBeforeSelectTram(tram);
        this.tramCollector.beforeSelect.on(this.beforeSelectTramHandler);
        this.afterSelectTramHandler = (tram: LeafletTram) => this.onAfterSelectTram(tram);
        this.tramCollector.afterSelect.on(this.afterSelectTramHandler);
        this.nextStopHandler = data => this.onNextStop(data);
        this.tramCollector.nextStop.on(this.nextStopHandler);

        this.routeCollector.afterInitialize.on(_ => {
            this.routes = this.routeCollector.routes;
        });
        this.afterBuildSegmentedRoute = (builtRoute: EnhancedBuiltRoute) => this.onAfterBuildSegmentedRoute(builtRoute);
        this.routeCollector.afterBuildSegmentedRoute.on(this.afterBuildSegmentedRoute);

        this.showRouteInfoPanelHandler = isShown => {
            if (!isShown && this.selectedRoute) {
                this.selectedRoute = null;
            }
        };
        this.exchanger.showRouteInfoPanelHandler.on(this.showRouteInfoPanelHandler);
    }

    ngOnDestroy() {
        this.activatedRouteParams = undefined;

        this.stopCollector.afterSelect.off(this.afterSelectStopHandler);
        this.railCollector.afterSelect.off(this.afterSelectRailHandler);
        this.tramCollector.afterSelect.off(this.afterSelectTramHandler);
        this.tramCollector.nextStop.off(this.nextStopHandler);
        this.routeCollector.afterBuildSegmentedRoute.off(this.afterBuildSegmentedRoute);

        this.exchanger.showRouteInfoPanelHandler.off(this.showRouteInfoPanelHandler);

        this.afterSelectStopHandler = null;
        this.afterSelectRailHandler = null;
        this.afterSelectTramHandler = null;
        this.nextStopHandler = null;
        this.showRouteInfoPanelHandler = null;
        this.afterBuildSegmentedRoute = null;
    }

    select(route: Route) {
        if (this.selectedRoute === route) {
            this.selectedRoute = null;
            this.routeCollector.unselect();
            return;
        }

        this.stopCollector.unselect(true);
        this.exchanger.showRouteInfoPanel(true);
        this.selectedRoute = route;
        if (this.mapObjectType === MapObjectType.Stop)
            this.isReverse = this.selectedRoute.reverseStopIds.indexOf((this.mapObject as LeafletStop).id) >= 0;
        if (this.mapObjectType === MapObjectType.Rail)
            this.isReverse = this.selectedRoute.reverseRailIds.indexOf((this.mapObject as LeafletStop).id) >= 0;
        this.stops = this.stopCollector.getWithSchedule(route, this.isReverse);
        //TODO show schedule for stops

        this.routeCollector.select(route, this.isReverse);
    }

    toggle(route: Route) {
        if (route.isEnabled)
            this.routeCollector.disable(route);
        else
            this.routeCollector.enable(route);

        route.isEnabled = !route.isEnabled;

        if (this.selectedRoutes.length > 0)
            this.filterSelectedRoutes(route);
        this.filterTram(route);
    }

    hover(id: number, isSelected: boolean) {
        if (isSelected)
            this.stopCollector.highlight(id);
        else
            this.stopCollector.unhighlight(id);
    }

    switchStop() {
        var value = this.departureStop;
        this.departureStop = this.arrivalStop;
        if (this.departureStop)
            this.stopCollector.selectGently(this.departureStop.id, StopSelectionType.Start);
        this.arrivalStop = value;
        if (this.arrivalStop)
            this.stopCollector.selectGently(this.arrivalStop.id, StopSelectionType.End);

        if (this.builtRoutes && this.builtRoutes.length) {
            this.builtRoutes = [];
        }
    }

    selectStopToBuildRoute(stop: LeafletStop, pointType: string) {
        this.isBuildRoutePanelOpened = true;

        if (pointType === 'from') {
            this.departureStop = stop;
            this.stopCollector.select(stop.id, StopSelectionType.Start);

            if (this.arrivalStop === stop)
                this.arrivalStop = null;
        } else if (pointType === 'to') {
            this.arrivalStop = stop;
            this.stopCollector.select(stop.id, StopSelectionType.End);

            if (this.departureStop === stop)
                this.departureStop = null;
        }

        if (this.builtRoutes && this.builtRoutes.length) {
            this.builtRoutes = [];
        }
    }

    closeBuildRoutePanel() {
        this.unselectBuiltRoute();

        this.arrivalStop = null;
        this.departureStop = null;
        this.isBuildRoutePanelOpened = false;
        this.builtRoutes = [];
    }

    closePanel() {
        this.exchanger.showInfoPanel(false);
    }

    buildRoute() {
        if (!this.arrivalStop || !this.departureStop)
            return;

        this.isLoading = true;

        this.mapObject = null;
        this.selectedRoutes = [];

        let startStopId = this.departureStop.id;
        let finishStopId = this.arrivalStop.id;

        this.stopCollector.unselect();
        this.routeCollector.getSegmentedRoutes(startStopId, finishStopId)
            .then(routes => {
                if (!routes || !routes.length) {
                    this.isLoading = false;
                    return;
                }
                this.builtRoutes = routes;

                if (this.builtRoutes && this.builtRoutes.length) {
                    this.showBuiltRoutePanel(this.builtRoutes[0]);
                }
                this.isLoading = false;
            }).catch(reason => {
                this.isLoading = false;
            });
    }

    showBuiltRoutePanel(route: EnhancedBuiltRoute) {
        this.exchanger.showBuiltRouteInfoPanel(true);

        if (route.isSelected)
            return;

        this.unselectBuiltRoute();
        this.routeCollector.select(route);
    }

    private reset() {
        this.mapObject = null;
        this.mapObjectType = MapObjectType.Unknown;
        this.selectedRoute = null;
        this.selectedRoutes = [];
        this.stops = [];
        this.isReverse = false;
        this.isDisabled = false;
        this.outerStops = [];
    }

    private filterSelectedRoutes(toggledRoute: Route) {
        this.selectedRoutes.some(r => {
            if (r.number === toggledRoute.number) {
                r.isEnabled = toggledRoute.isEnabled;

                if (this.selectedRoute && this.selectedRoute.id === toggledRoute.id)
                    this.selectedRoute = null;

                return true;
            }
            return false;
        });

        this.isDisabled = this.selectedRoutes.every(r => !r.isEnabled);

        if (this.isDisabled)
            this.reset();
    }
    private filterTram(toggledRoute: Route) {
        if (this.mapObject instanceof LeafletTram) {
            let route = this.routeCollector.get(this.mapObject);

            if (route.number === toggledRoute.number)
                this.reset();
        }
    }

    private onAfterInitializeStops() {
        this.predefineSegmentedRoute();
    }

    private onAfterSelectStop(stop: LeafletStop) {
        this.reset();
        this.mapObject = stop;
        this.builtRoutes = [];
        this.exchanger.showBuiltRouteInfoPanel(false);

        if (!this.routes)
            return;
        this.selectedRoutes = this.routes.filter(r => r.allStopIds.indexOf(stop.id) >= 0);
    }
    private onAfterSelectRail(e: any, rail: LeafletRail) {
        this.reset();
        this.mapObject = rail;

        this.closeBuildRoutePanel();
        this.exchanger.showBuiltRouteInfoPanel(false);
        this.selectedRoutes = this.routes.filter(r => r.allRailIds.indexOf(rail.id) >= 0);
    }
    private onBeforeSelectTram(tram: LeafletTram) {
        this.closeBuildRoutePanel();
    }
    private onAfterSelectTram(tram: LeafletTram) {
        this.reset();
        this.mapObject = tram;

        this.exchanger.showBuiltRouteInfoPanel(false);
        this.prepareScheduledStops(tram);
    }
    private onNextStop(data) {
        let tramId = data.tramId;

        if (this.mapObject === undefined)
            return;
        if (!(this.mapObject instanceof LeafletTram))
            return;
        if (tramId !== this.mapObject.id)
            return;

        // let tram = this.tramCollector.get(tramId);
        // if (this.mapObject.scheduleId !== tram.scheduleId) {
        //     //or maybe need to observe scheduleId
        //     // this.prepareScheduledStops(tram);
        //     return;
        // }

        if (data.nextStop)
            this.calcNextStop(data.nextStop);
    }
    private onAfterBuildSegmentedRoute(builtRoute: EnhancedBuiltRoute) {
        if (this.isBuildRoutePanelOpened !== true)
            this.isBuildRoutePanelOpened = true;
    }

    private prepareScheduledStops(tram: Tram) {
        this.isLoading = true;
        this.isReverse = tram.isReverseTrip;
        this.scheduleCollector.get(tram).then(schedule => {
            if (!schedule.stopSchedules) {
                this.stops = [];
                return;
            }

            this.stops = schedule.stopSchedules.map(ss => {
                let stop = this.stopCollector.get(ss.stopId);
                if (!stop)
                    return;

                let scheduledStop = new ScheduledStop(stop);
                scheduledStop.arrivalMoment = ss.arrivalTime;

                return scheduledStop;
            });

            return this.stops;
        }).catch(_ => {
            let route = this.routeCollector.get(tram);
            this.stops = this.stopCollector.get(route, this.isReverse) as ScheduledStop[];
            return this.stops;
        }).then(_ => {
            this.prepareOutterStops();
            this.isLoading = false;

            if (tram.nextStop) {
                this.calcNextStop(tram.nextStop);
            }
        });
    }

    private prepareOutterStops() {
        if (this.stops.length >= 1) {
            this.outerStops = [this.stops[0], this.stops[this.stops.length - 1]];
        }
    }

    private calcNextStop(nextStop: TramStop) {
        let index = this.stops.findIndex(s => s.id === nextStop.stopId);
        if (index === -1)
            return;

        this.stops.forEach((s, i) => {
            if (i < index) {
                s.isPassed = true;
                s.isActive = false;
            } else if (i == index) {
                s.isPassed = false;
                s.isActive = true;
                if (nextStop && nextStop.arrivalTime) {
                    s.arrivalMoment = nextStop.arrivalTime;
                }
            } else {
                s.isPassed = false;
                s.isActive = false;
            }
        });
    }

    private unselectBuiltRoute() {
        let selectedBuiltRoute: EnhancedBuiltRoute;
        let isFound = this.builtRoutes.some(r => {
            if (r.isSelected) {
                selectedBuiltRoute = r;
                return true;
            }
            return false;
        });
        this.routeCollector.unselectDeeply(selectedBuiltRoute);
    }

    private predefineSegmentedRoute() {
        if (!this.activatedRouteParams)
            return;

        let startStopId: number;
        let finishStopId: number;

        if (this.activatedRouteParams.has('startStopId')) {
            startStopId = +this.activatedRouteParams.get('startStopId');
        }

        if (this.activatedRouteParams.has('finishStopId')) {
            finishStopId = +this.activatedRouteParams.get('finishStopId');
        }

        if (startStopId && finishStopId) {
            this.isBuildRoutePanelOpened = true;

            let departureStop = this.stopCollector.get(startStopId);
            this.selectStopToBuildRoute(departureStop as LeafletStop, 'from');
            let arrivalStop = this.stopCollector.get(finishStopId);
            this.selectStopToBuildRoute(arrivalStop as LeafletStop, 'to');

            this.buildRoute();
        }
    }
}

enum MapObjectType {
    Unknown = 'Unknown',
    Stop = 'Stop',
    Rail = 'Rail',
    Tram = 'Tram'
}


// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/common/info-panel.component.ts